import Papa from 'papaparse';
import BPromise from 'bluebird';
import dateParser from './dateParser';
import { AppUser } from 'Src/views/App/App';
import { createForm, fetchAllForms } from 'Src/helpers/service/admin/forms';
import { createHouse, fetchAllHouses } from 'Src/helpers/service/admin/houses';
import { ImportError } from 'Src/helpers/utils/types';
import moment from 'moment';

interface CSVParseResult {
  data: any[];
  errors: any[];
  meta: { fields: string[] };
}

export const parseCSVFile = (file: File, config: object): Promise<CSVParseResult> => {
  return new BPromise((resolve, reject) => {
    const effectiveConfig = {
      ...config,
      complete: (results: CSVParseResult) => resolve(results),
      error: (error: Error) => reject(error)
    };

    Papa.parse(file, effectiveConfig);
  });
};

export const guessTable = {
  firstName: ['firstName', 'first_name', 'firstname', 'name', 'forename', 'First Name', 'Name', 'Forename'],
  lastName: ['lastName', 'last_name', 'lastname', 'surname', 'Last Name', 'Surname'],
  gender: ['gender', 'sex', 'Gender', 'Sex'],
  birthday: ['birthday', 'date_of_birth', 'dob', 'Birthday', 'Date of Birth', 'DOB'],
  year: ['year', 'grade', 'classYear', 'Year', 'Grade', 'Class Year'],
  form: ['form', 'classForm', 'Form', 'Class Form', 'Class'],
  house: ['house', 'team', 'houseName', 'House', 'House Name']
};

export const guessColumn = (fieldNames: string[], fieldToGuess: string): string | undefined => {
  const possibleValues = guessTable[fieldToGuess];
  if (!Array.isArray(possibleValues)) return undefined;
  return fieldNames.find(fieldName =>
    possibleValues.some(value => value.toLowerCase() === fieldName.trim().toLowerCase())
  );
};

export const guessHeaders = (fieldNamesArray: string[]) => {
  const headers = {
    firstName: guessColumn(fieldNamesArray, 'firstName'),
    lastName: guessColumn(fieldNamesArray, 'lastName'),
    gender: guessColumn(fieldNamesArray, 'gender'),
    birthday: guessColumn(fieldNamesArray, 'birthday'),
    year: guessColumn(fieldNamesArray, 'year'),
    form: guessColumn(fieldNamesArray, 'form'),
    house: guessColumn(fieldNamesArray, 'house')
  };
  return headers;
};

export const normalizeYear = (yearValue: string): number | undefined => {
  const yearString = yearValue.trim().toLowerCase();

  if (['r', 'reception'].includes(yearString)) {
    return 0;
  }

  // Extract numeric part from variations like "Y1", "Year 1", "year1", etc.
  const match = yearString.match(/\d+/);
  if (match) {
    return parseInt(match[0], 10);
  }

  return undefined;
};

export const objectToStudent = (headers: any, obj: any) => {
  const student = {
    firstName: obj[headers.firstName]?.trim(),
    lastName: obj[headers.lastName]?.trim(),
    gender: obj[headers.gender] ? guessGender(obj[headers.gender]) : undefined,
    birthday: obj[headers.birthday] ? dateParser(obj[headers.birthday]) : undefined,
    formName: obj[headers.form]?.trim(),
    houseName: obj[headers.house]?.trim(),
    year: obj[headers.year] ? normalizeYear(obj[headers.year].toString()) : undefined
  };
  return student;
};

export const guessGender = (genderValue: string): string => {
  const lowGender = String(genderValue || '')
    .trim()
    .toLowerCase();
  if (['boy', 'male', 'm', '1'].includes(lowGender)) return 'MALE';
  if (['girl', 'female', 'f', '0'].includes(lowGender)) return 'FEMALE';
  return String(genderValue || '').trim();
};

export const readStudentsFromCSVFile = async (
  file: File
): Promise<{ students: any[]; errors: ImportError[]; meta: any }> => {
  const result = await parseCSVFile(file, { header: true, skipEmptyLines: true });
  const data = result.data || [];
  const origHeaders = result.meta.fields || [];
  const guessedHeaders = guessHeaders(origHeaders);

  const { isValid, missingHeaders } = validateHeader(guessedHeaders);
  const errors: ImportError[] = [];

  if (!isValid) {
    errors.push({
      type: 'MissingHeaders',
      code: 'MissingHeaders',
      message: `The following mandatory headers are missing: ${missingHeaders.join(', ')}`,
      row: 0
    });
    return {
      students: [],
      errors,
      meta: { ...result.meta, guessedHeaders }
    };
  }

  const mandatoryFields = ['firstName', 'lastName', 'gender', 'year'];
  const seenStudents = new Set<string>();
  const studentArray = data
    .filter(item => Object.keys(item).length > 1)
    .map((item, index) => {
      const student = objectToStudent(guessedHeaders, item);
      const missingFields = mandatoryFields.filter(field =>
        field === 'year' ? student[field] === undefined : !student[field]
      );

      if (missingFields.length > 0) {
        errors.push({
          type: 'MissingData',
          code: 'MissingData',
          message: `Row ${index + 1} is missing mandatory fields: ${missingFields.join(', ')}`,
          row: index + 1
        });
      }

      if (student.gender && !['MALE', 'FEMALE'].includes(student.gender)) {
        errors.push({
          type: 'InvalidGender',
          code: 'InvalidGender',
          message: `Row ${index + 1} has an unsupported gender value. Use Male/Female, Boy/Girl, M/F or 1/0.`,
          row: index + 1
        });
      }

      const rawBirthday = guessedHeaders.birthday ? item[guessedHeaders.birthday] : undefined;
      if (
        rawBirthday &&
        !moment(
          String(rawBirthday).trim(),
          ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY/MM/DD', 'D/M/YYYY', 'M/D/YYYY'],
          true
        ).isValid()
      ) {
        errors.push({
          type: 'InvalidBirthday',
          code: 'InvalidBirthday',
          message: `Row ${index + 1} has an invalid date of birth: ${rawBirthday}.`,
          row: index + 1
        });
      }

      const identityParts = student.birthday
        ? [student.firstName, student.lastName, student.birthday]
        : [
            student.firstName,
            student.lastName,
            '',
            student.gender,
            student.year,
            student.formName || '',
            student.houseName || ''
          ];
      const duplicateKey = identityParts
        .map(value =>
          String(value || '')
            .trim()
            .toLowerCase()
        )
        .join('|');
      if (seenStudents.has(duplicateKey)) {
        errors.push({
          type: 'DuplicateStudent',
          code: 'DuplicateStudent',
          message: `Row ${index + 1} duplicates another student in this file: ${student.firstName} ${
            student.lastName
          }.`,
          row: index + 1
        });
      }
      seenStudents.add(duplicateKey);

      return student;
    });

  if (errors.length > 0) {
    return {
      students: [],
      errors,
      meta: { ...result.meta, guessedHeaders }
    };
  }

  return {
    students: studentArray.length > 0 ? studentArray : [],
    errors,
    meta: { ...result.meta, guessedHeaders }
  };
};

export const pullFormsHouses = async (result: any, school: any, user: AppUser) => {
  const activeForms = await fetchAllForms(user);
  const activeHouses = await fetchAllHouses(user);

  const filteredForms = activeForms.filter((form: any) => form.status === 'ACTIVE');
  const filteredHouses = activeHouses.filter((house: any) => house.status === 'ACTIVE');

  const createdFormsMap = new Map<string, any>();
  const createdHousesMap = new Map<string, any>();
  result.createdFormIds = [];
  result.createdHouseIds = [];

  const formCreationLock = {};
  const houseCreationLock = {};

  if (!Array.isArray(result.students)) {
    console.error('Result.students is not an array:', result.students);
    return result;
  }

  const findOrCreateForm = async (formName: string, age: number) => {
    const safeFormName = String(formName || '').trim();
    const isReception = ['reception', 'r', 'year 0', '0', 'year0'].includes(safeFormName.toLowerCase()) || age === 0;
    const targetFormName = isReception ? 'Reception' : formName;
    const targetAge = isReception ? 0 : age;

    const formIdentifier = `${targetFormName.trim().toLowerCase()}-${targetAge}`;

    if (formCreationLock[formIdentifier]) {
      await formCreationLock[formIdentifier];
    }

    let form =
      createdFormsMap.get(formIdentifier) ||
      filteredForms.find(
        (item: any) =>
          String(item.name || '')
            .trim()
            .toLowerCase() === targetFormName.trim().toLowerCase() && Number(item.age) === Number(targetAge)
      );
    if (!form) {
      formCreationLock[formIdentifier] = createForm(user, { name: targetFormName, age: targetAge });

      try {
        form = await formCreationLock[formIdentifier];
        if (form) {
          filteredForms.push(form);
          createdFormsMap.set(formIdentifier, form);
          result.createdFormIds.push(form.id);
        }
      } catch (error) {
        console.error(`Error creating form: ${error.message}`);
        return { error: true };
      } finally {
        delete formCreationLock[formIdentifier];
      }
    }

    return form;
  };

  const findOrCreateHouse = async (houseName: string) => {
    const houseIdentifier = houseName.trim().toLowerCase();

    if (houseCreationLock[houseIdentifier]) {
      await houseCreationLock[houseIdentifier];
    }

    let house =
      createdHousesMap.get(houseIdentifier) ||
      filteredHouses.find(
        (item: any) =>
          String(item.name || '')
            .trim()
            .toLowerCase() === houseIdentifier
      );
    if (!house) {
      houseCreationLock[houseIdentifier] = createHouse(user, { name: houseName });

      try {
        house = await houseCreationLock[houseIdentifier];
        if (house) {
          filteredHouses.push(house);
          createdHousesMap.set(houseIdentifier, house);
          result.createdHouseIds.push(house.id);
        }
      } catch (error) {
        console.error(`Error creating house: ${error.message}`);
        return { error: true };
      } finally {
        delete houseCreationLock[houseIdentifier];
      }
    }

    return house;
  };

  result.students = await Promise.all(
    result.students.map(async (student: any, i: number) => {
      let formNameToUse = student.formName;
      if (!formNameToUse && student.year !== undefined) {
        formNameToUse = Number(student.year) === 0 ? 'Reception' : `Year ${student.year}`;
      }

      const studentAge = parseInt(student.year, 10);
      if (isNaN(studentAge)) {
        result.errors.push({
          type: 'InvalidYear',
          code: 'InvalidYear',
          message: `Invalid year for ${student.firstName} ${student.lastName}: ${student.year}`,
          row: i + 1
        });
        return student;
      }

      const form = await findOrCreateForm(formNameToUse, studentAge);
      if (form && !form.error) {
        student.formId = form.id;
      } else {
        result.errors.push({
          type: 'StudentFormCreationFailed',
          code: 'StudentFormCreationFailed',
          message: `Failed to create form: ${formNameToUse}. Student: ${student.firstName} ${student.lastName}`,
          row: i
        });
      }

      const houseName = student.houseName;
      if (houseName) {
        const house = await findOrCreateHouse(houseName);
        if (house && !house.error) {
          student.houseId = house.id;
        } else {
          result.errors.push({
            type: 'StudentHouseCreationFailed',
            code: 'StudentHouseCreationFailed',
            message: `Failed to create house: ${houseName}. Student: ${student.firstName} ${student.lastName}`,
            row: i
          });
        }
      }

      return student;
    })
  );

  return result;
};

export const validateHeader = (headers: any) => {
  const mandatoryHeaders = ['firstName', 'lastName', 'gender', 'year'];
  const missingHeaders: string[] = [];

  mandatoryHeaders.forEach(header => {
    if (!headers[header]) {
      console.error(`Missing mandatory header: ${header}`);
      missingHeaders.push(header);
    }
  });

  return { isValid: missingHeaders.length === 0, missingHeaders };
};
